import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, RotateCcw, Search, SlidersHorizontal } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const COLLAPSED_CATEGORY_LIMIT = 10;

export default function SparePartsFiltersSection({
  query,
  onQueryChange,
  category,
  onCategoryChange,
  categories,
  visibleStart,
  visibleEnd,
  totalCount,
}) {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleCategories = categories.filter((item) => String(item?.value || item || '').trim());
  const primaryCategories = useMemo(() => {
    if (visibleCategories.length <= COLLAPSED_CATEGORY_LIMIT) return visibleCategories;
    const initialCategories = visibleCategories.slice(0, COLLAPSED_CATEGORY_LIMIT);
    const activeCategory = visibleCategories.find((item) => String(item?.value || item || '').trim() === category);
    if (!activeCategory || initialCategories.some((item) => String(item?.value || item || '').trim() === category)) {
      return initialCategories;
    }

    return [...initialCategories.slice(0, COLLAPSED_CATEGORY_LIMIT - 1), activeCategory];
  }, [category, visibleCategories]);
  const primaryCategoryValues = useMemo(
    () => new Set(primaryCategories.map((item) => String(item?.value || item || '').trim())),
    [primaryCategories],
  );
  const secondaryCategories = useMemo(
    () => visibleCategories.filter((item) => !primaryCategoryValues.has(String(item?.value || item || '').trim())),
    [primaryCategoryValues, visibleCategories],
  );
  const hiddenCategoryCount = secondaryCategories.length;
  const hasExpandableCategories = visibleCategories.length > COLLAPSED_CATEGORY_LIMIT;
  const visibleLabel =
    totalCount === 0 || visibleStart === visibleEnd ? String(visibleEnd || visibleStart || 0) : `${visibleStart}-${visibleEnd}`;
  const hasActiveFilters = String(query || '').trim() !== '' || category !== 'all';
  const handleResetFilters = () => {
    onQueryChange('');
    onCategoryChange('all');
    setIsExpanded(false);
  };
  const renderCategoryButton = (item) => {
    const value = String(item?.value || item || '').trim();
    const isActive = category === value;
    const label = item?.label || item || t('common.general');

    return (
      <button
        key={value}
        type="button"
        onClick={() => onCategoryChange(value)}
        className={`inline-flex h-9 items-center rounded-full border px-5 text-[11px] font-bold uppercase tracking-[0.14em] transition-all duration-300 ${
          isActive
            ? 'border-[var(--dash-main)] bg-[var(--dash-main)] text-white shadow-[0_10px_24px_rgba(var(--dash-main-rgb),0.24)]'
            : 'border-white/15 bg-white/[0.018] text-white/62 hover:border-white/30 hover:bg-white/[0.035] hover:text-white/86'
        }`}
      >
        {label}
      </button>
    );
  };
  const renderExpandButton = () => {
    if (!hasExpandableCategories) return null;

    return (
      <button
        type="button"
        onClick={() => setIsExpanded((current) => !current)}
        aria-expanded={isExpanded}
        aria-label={
          isExpanded
            ? t('sparePartsPage.filters.showLessCategories', 'Show fewer categories')
            : t('sparePartsPage.filters.showMoreCategories', 'Show more categories')
        }
        className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full border border-white/18 bg-white/[0.018] px-4 text-[11px] font-bold uppercase tracking-[0.12em] text-white/72 transition-all duration-300 hover:border-white/32 hover:bg-white/[0.04] hover:text-white"
      >
        {isExpanded ? (
          <>
            <span>{t('sparePartsPage.filters.less', 'Less')}</span>
            <span className="flex h-5 w-5 items-center justify-center rounded-full border border-white/12 bg-white/[0.08] text-white">
              <ChevronUp className="h-3.5 w-3.5 transition-transform duration-300" aria-hidden="true" />
            </span>
          </>
        ) : (
          <>
            <span>+{hiddenCategoryCount}</span>
            <span className="flex h-5 w-5 items-center justify-center rounded-full border border-white/12 bg-white/[0.08] text-white">
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300" aria-hidden="true" />
            </span>
          </>
        )}
      </button>
    );
  };

  return (
    <section className="relative overflow-hidden bg-transparent pt-0 pb-10">
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-10">
        <div className="relative z-20 overflow-hidden rounded-[1.75rem] border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.075),rgba(255,255,255,0.018))] p-px backdrop-blur-md">
          <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-[var(--dash-main)]/55 to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 h-24 w-56 translate-x-16 -translate-y-10 rounded-full bg-[var(--dash-main)]/10 blur-3xl" />
          <div className="relative rounded-[1.68rem] bg-[rgba(15,15,20,0.94)] p-4 sm:p-5">
            <div className="flex flex-col gap-5">
              <div className="grid gap-4 lg:grid-cols-[minmax(15rem,0.7fr)_minmax(18rem,1.3fr)] lg:items-center">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.045] text-[var(--dash-main)] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                    <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold uppercase tracking-[-0.01em] text-white sm:text-xl">
                      {t('sparePartsPage.filters.title')}
                    </h2>
                    <p className="mt-1 text-xs font-medium text-white/48">
                      {t('sparePartsPage.filters.showing')} {visibleLabel} {t('sparePartsPage.filters.of')} {totalCount}{' '}
                      {t('sparePartsPage.filters.parts')}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <label className="block flex-1">
                    <span className="sr-only">{t('sparePartsPage.filters.searchPlaceholder', 'Search spare parts...')}</span>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" aria-hidden="true" />
                      <input
                        placeholder={t('sparePartsPage.filters.searchPlaceholder', 'Search spare parts...')}
                        value={query}
                        onChange={(event) => onQueryChange(event.target.value)}
                        className="h-12 w-full rounded-2xl border border-white/18 bg-white/8 pl-11 pr-5 text-sm font-medium text-white placeholder:text-slate-400 outline-none transition-all duration-300 focus:border-[var(--dash-main)] focus:bg-white/12 focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgba(2,6,23,0.94)]"
                      />
                    </div>
                  </label>
                  {hasActiveFilters ? (
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.035] px-4 text-[11px] font-bold uppercase tracking-[0.12em] text-white/70 transition-all duration-300 hover:border-white/25 hover:bg-white/[0.06] hover:text-white"
                    >
                      <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                      {t('common.reset', 'Reset')}
                    </button>
                  ) : null}
                </div>
              </div>

              {visibleCategories.length > 0 ? (
                <div className="rounded-[1.35rem] border border-white/10 bg-[linear-gradient(180deg,rgba(0,0,0,0.18),rgba(255,255,255,0.015))] p-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.035)] sm:p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/42">
                      {t('sparePartsPage.filters.categories', 'Categories')}
                    </p>
                    {renderExpandButton()}
                  </div>
                  <div className="flex flex-col gap-2 overflow-hidden pr-1">
                      <div className="flex flex-wrap gap-2">
                        {primaryCategories.map(renderCategoryButton)}
                      </div>
                      {hasExpandableCategories ? (
                        <div
                          className={`grid transition-[grid-template-rows,opacity,transform] duration-500 ease-out ${
                            isExpanded
                              ? 'grid-rows-[1fr] translate-y-0 opacity-100'
                              : 'grid-rows-[0fr] -translate-y-1 opacity-0'
                          }`}
                        >
                          <div className="min-h-0 overflow-hidden">
                            <div className="flex flex-wrap gap-2 pt-1.5">
                              {secondaryCategories.map(renderCategoryButton)}
                            </div>
                          </div>
                        </div>
                      ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
